<!--
  Dokumenty wewnętrzne kierowcy (np. podpisane zobowiązanie). Widzi je tylko
  biuro: karta kierowcy (z `driver-id`) i repozytorium (bez — wszystkie,
  z kolumną kierowcy). Apka kierowcy nie ma do nich żadnej trasy.
-->
<template>
  <v-card :flat="flat" class="internal-docs">
    <v-card-title class="d-flex flex-wrap" style="gap: 8px;">
      <v-icon left color="deep-purple lighten-2">mdi-folder-lock-outline</v-icon>
      <span class="text-h6">Dokumenty wewnętrzne</span>
      <v-chip x-small label color="deep-purple lighten-4" text-color="deep-purple darken-3" class="ml-1">
        <v-icon x-small left>mdi-eye-off-outline</v-icon>widoczne tylko dla biura
      </v-chip>
      <v-spacer />
      <v-btn color="deep-purple" dark small @click="otworzDodawanie">
        <v-icon left small>mdi-plus</v-icon>Dodaj dokument wewnętrzny
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-text-field
        v-if="!driverId"
        v-model="szukaj"
        prepend-inner-icon="mdi-magnify"
        label="Szukaj po kierowcy, nazwie lub opisie"
        outlined dense clearable hide-details class="mb-3"
      />

      <div v-if="ladowanie" class="text-center py-6">
        <v-progress-circular indeterminate color="deep-purple" />
      </div>
      <div v-else-if="!widoczne.length" class="text-center grey--text py-6">
        {{ szukaj ? 'Brak dokumentów pasujących do wyszukiwania.' : 'Brak dokumentów wewnętrznych.' }}
      </div>

      <div v-for="d in widoczne" :key="d.id" class="doc-row">
        <div class="d-flex align-start flex-wrap" style="gap: 8px;">
          <div style="flex: 1; min-width: 220px;">
            <div class="font-weight-bold">{{ d.title }}</div>
            <div v-if="!driverId && d.driver" class="caption">
              <v-icon x-small>mdi-account</v-icon>
              <a :href="'/driver-details?id=' + d.driver.id">{{ d.driver.firstName }} {{ d.driver.lastName }}</a>
            </div>
            <div v-if="d.description" class="caption doc-desc">{{ d.description }}</div>
            <div class="caption grey--text">
              Dodano {{ data(d.createdAt) }}<span v-if="d.createdByName"> · {{ d.createdByName }}</span>
            </div>
          </div>
          <div>
            <v-btn icon small title="Edytuj nazwę i opis" @click="otworzEdycje(d)"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn icon small title="Dodaj pliki" @click="wybierzDoDopisania(d)"><v-icon small>mdi-paperclip-plus</v-icon></v-btn>
            <v-btn icon small color="error" title="Usuń dokument" @click="usunDokument(d)"><v-icon small>mdi-delete</v-icon></v-btn>
          </div>
        </div>
        <div class="mt-1">
          <v-chip
            v-for="f in d.files" :key="f.id"
            small outlined class="mr-1 mb-1"
            :close="d.files.length > 1"
            close-icon="mdi-close"
            @click="otworzPlik(f)"
            @click:close="usunPlik(d, f)"
          >
            <v-icon small left :color="ikonaKolor(f)">{{ ikona(f) }}</v-icon>
            {{ f.originalName }}
            <span class="grey--text ml-1">{{ rozmiar(f.size) }}</span>
          </v-chip>
        </div>
      </div>

      <input ref="dopiszInput" type="file" multiple style="display: none;" @change="dopiszPliki" />
    </v-card-text>

    <!-- Dodawanie -->
    <v-dialog v-model="dodawanie" max-width="600" content-class="dark-form-dialog">
      <v-card>
        <v-card-title>Nowy dokument wewnętrzny</v-card-title>
        <v-card-text>
          <v-form ref="formDodaj">
            <v-autocomplete
              v-if="!driverId"
              v-model="nowy.driverId"
              :items="kierowcy"
              item-text="label" item-value="id"
              label="Kierowca *" outlined dense
              :loading="ladowanieKierowcow"
              :rules="[v => !!v || 'Wybierz kierowcę']"
              no-data-text="Brak kierowcy o takiej nazwie"
            />
            <v-text-field
              v-model="nowy.title"
              label="Nazwa dokumentu *" outlined dense
              placeholder="np. Zobowiązanie do zwrotu kaucji"
              :rules="[v => !!(v && v.trim()) || 'Podaj nazwę']"
            />
            <v-textarea
              v-model="nowy.description"
              label="Opis (opcjonalnie)" outlined dense rows="3" auto-grow
              placeholder="np. podpisane w biurze 29.10, oryginał w segregatorze"
            />
            <v-file-input
              v-model="nowy.files"
              label="Pliki * (PDF, zdjęcia, Word, Excel…)"
              multiple chips show-size counter outlined dense
              prepend-icon="mdi-paperclip"
              :rules="[v => (v && v.length > 0) || 'Dodaj co najmniej jeden plik', v => !v || v.length <= 20 || 'Najwyżej 20 plików', v => !v || v.every(f => f.size <= 25 * 1024 * 1024) || 'Plik większy niż 25 MB']"
            />
          </v-form>
          <div class="caption grey--text">
            Kierowca nie zobaczy tego dokumentu w aplikacji. Widzi go tylko biuro
            na karcie kierowcy i w repozytorium dokumentów.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dodawanie = false">Anuluj</v-btn>
          <v-btn color="deep-purple" dark :loading="zapisywanie" @click="zapiszNowy">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edycja -->
    <v-dialog v-model="edycja" max-width="560" content-class="dark-form-dialog">
      <v-card>
        <v-card-title>Edytuj dokument</v-card-title>
        <v-card-text>
          <v-text-field v-model="edytowany.title" label="Nazwa dokumentu *" outlined dense />
          <v-textarea v-model="edytowany.description" label="Opis" outlined dense rows="3" auto-grow />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="edycja = false">Anuluj</v-btn>
          <v-btn color="deep-purple" dark :loading="zapisywanie" :disabled="!(edytowany.title || '').trim()" @click="zapiszEdycje">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

const API = process.env.VUE_APP_ROOT_API + "driver-internal-documents";
const naglowki = () => ({ Authorization: "Bearer " + localStorage.getItem("token") });

export default {
  name: "InternalDocumentsSection",
  props: {
    // Z kartą kierowcy — tylko jego dokumenty; bez — całe repozytorium.
    driverId: { type: String, default: null },
    flat: { type: Boolean, default: false },
  },
  data() {
    return {
      dokumenty: [],
      ladowanie: false,
      szukaj: "",
      dodawanie: false,
      edycja: false,
      zapisywanie: false,
      nowy: { driverId: null, title: "", description: "", files: [] },
      edytowany: { id: null, title: "", description: "" },
      doDopisania: null,
      kierowcy: [],
      ladowanieKierowcow: false,
    };
  },
  computed: {
    widoczne() {
      const q = (this.szukaj || "").trim().toLowerCase();
      if (!q) return this.dokumenty;
      return this.dokumenty.filter((d) =>
        [d.title, d.description, d.driver && `${d.driver.firstName} ${d.driver.lastName}`,
          ...(d.files || []).map((f) => f.originalName)]
          .filter(Boolean).join(" ").toLowerCase().includes(q),
      );
    },
  },
  watch: {
    driverId() { this.wczytaj(); },
  },
  created() { this.wczytaj(); },
  methods: {
    async wczytaj() {
      this.ladowanie = true;
      try {
        const r = await axios.get(API, {
          headers: naglowki(),
          params: this.driverId ? { driverId: this.driverId } : {},
        });
        this.dokumenty = r.data?.data || [];
        this.$emit("count", this.dokumenty.length);
      } catch (e) {
        this.dokumenty = [];
        this.blad(e, "Nie udało się wczytać dokumentów wewnętrznych");
      }
      this.ladowanie = false;
    },
    async otworzDodawanie() {
      this.nowy = { driverId: this.driverId, title: "", description: "", files: [] };
      this.dodawanie = true;
      this.$nextTick(() => this.$refs.formDodaj && this.$refs.formDodaj.resetValidation());
      if (this.driverId || this.kierowcy.length) return;
      this.ladowanieKierowcow = true;
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + "drivers?take=10000&skip=1&search=", { headers: naglowki() });
        this.kierowcy = (r.data?.data?.result || [])
          .map((d) => ({ id: d.id, label: `${d.firstName || ""} ${d.lastName || ""}`.trim() || d.email }))
          .sort((a, b) => a.label.localeCompare(b.label, "pl"));
      } catch (e) {
        this.blad(e, "Nie udało się wczytać listy kierowców");
      }
      this.ladowanieKierowcow = false;
    },
    async zapiszNowy() {
      if (!this.$refs.formDodaj.validate()) return;
      const f = new FormData();
      f.append("driverId", this.nowy.driverId);
      f.append("title", this.nowy.title.trim());
      f.append("description", (this.nowy.description || "").trim());
      this.nowy.files.forEach((p) => f.append("files", p, p.name));
      this.zapisywanie = true;
      try {
        await axios.post(API, f, { headers: naglowki() });
        this.dodawanie = false;
        await this.wczytaj();
        Swal.fire({ icon: "success", title: "Dokument dodany", timer: 1500, showConfirmButton: false });
      } catch (e) {
        this.blad(e, "Nie udało się dodać dokumentu");
      }
      this.zapisywanie = false;
    },
    otworzEdycje(d) {
      this.edytowany = { id: d.id, title: d.title, description: d.description || "" };
      this.edycja = true;
    },
    async zapiszEdycje() {
      this.zapisywanie = true;
      try {
        await axios.patch(`${API}/${this.edytowany.id}`, {
          title: this.edytowany.title,
          description: this.edytowany.description || "",
        }, { headers: naglowki() });
        this.edycja = false;
        await this.wczytaj();
      } catch (e) {
        this.blad(e, "Nie udało się zapisać zmian");
      }
      this.zapisywanie = false;
    },
    wybierzDoDopisania(d) {
      this.doDopisania = d;
      this.$refs.dopiszInput.value = "";
      this.$refs.dopiszInput.click();
    },
    async dopiszPliki(ev) {
      const pliki = Array.from(ev.target.files || []);
      if (!pliki.length || !this.doDopisania) return;
      const zaDuzy = pliki.find((p) => p.size > 25 * 1024 * 1024);
      if (zaDuzy) {
        Swal.fire("Za duży plik", `„${zaDuzy.name}" ma więcej niż 25 MB.`, "error");
        return;
      }
      const f = new FormData();
      pliki.forEach((p) => f.append("files", p, p.name));
      try {
        await axios.post(`${API}/${this.doDopisania.id}/files`, f, { headers: naglowki() });
        await this.wczytaj();
      } catch (e) {
        this.blad(e, "Nie udało się dodać plików");
      }
    },
    async otworzPlik(f) {
      // Nowa karta otwarta od razu (w odpowiedzi na kliknięcie), bo po
      // `await` przeglądarka zablokowałaby ją jako wyskakujące okno.
      const okno = window.open("", "_blank");
      try {
        const r = await axios.get(`${API}/files/${f.id}/download`, { headers: naglowki(), responseType: "blob" });
        const url = URL.createObjectURL(new Blob([r.data], { type: f.mimeType }));
        const podglad = /^(application\/pdf|image\/|text\/plain)/.test(f.mimeType);
        if (podglad && okno) {
          okno.location.href = url;
        } else {
          if (okno) okno.close();
          const a = document.createElement("a");
          a.href = url;
          a.download = f.originalName || "plik";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        if (okno) okno.close();
        this.blad(e, "Nie udało się pobrać pliku");
      }
    },
    async usunPlik(d, f) {
      const ok = await Swal.fire({
        title: "Usunąć plik?", text: f.originalName, icon: "warning",
        showCancelButton: true, confirmButtonText: "Tak, usuń", cancelButtonText: "Anuluj",
      });
      if (!ok.isConfirmed) return;
      try {
        await axios.delete(`${API}/files/${f.id}`, { headers: naglowki() });
        await this.wczytaj();
      } catch (e) {
        this.blad(e, "Nie udało się usunąć pliku");
      }
    },
    async usunDokument(d) {
      const ok = await Swal.fire({
        title: "Usunąć dokument?", text: `${d.title} (plików: ${d.files.length})`, icon: "warning",
        showCancelButton: true, confirmButtonText: "Tak, usuń", cancelButtonText: "Anuluj",
      });
      if (!ok.isConfirmed) return;
      try {
        await axios.delete(`${API}/${d.id}`, { headers: naglowki() });
        await this.wczytaj();
      } catch (e) {
        this.blad(e, "Nie udało się usunąć dokumentu");
      }
    },
    async blad(e, domyslny) {
      let msg = e?.response?.data?.message;
      const blob = e?.response?.data;
      if (blob && typeof blob.text === "function") {
        try { msg = JSON.parse(await blob.text()).message; } catch (_) { /* zostaje domyślny */ }
      }
      if (Array.isArray(msg)) msg = msg.join(", ");
      Swal.fire("Błąd", msg || domyslny, "error");
    },
    data(d) {
      if (!d) return "—";
      const t = new Date(d);
      const p = (n) => String(n).padStart(2, "0");
      return `${p(t.getDate())}.${p(t.getMonth() + 1)}.${t.getFullYear()} ${p(t.getHours())}:${p(t.getMinutes())}`;
    },
    rozmiar(b) {
      if (!b) return "";
      if (b < 1024) return `${b} B`;
      if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
      return `${(b / 1024 / 1024).toFixed(1)} MB`;
    },
    ikona(f) {
      const t = f.mimeType || "";
      if (t === "application/pdf") return "mdi-file-pdf-box";
      if (t.startsWith("image/")) return "mdi-file-image";
      if (/word|document/.test(t)) return "mdi-file-word";
      if (/sheet|excel/.test(t)) return "mdi-file-excel";
      return "mdi-file";
    },
    ikonaKolor(f) {
      const t = f.mimeType || "";
      if (t === "application/pdf") return "red";
      if (t.startsWith("image/")) return "teal";
      if (/word|document/.test(t)) return "blue";
      if (/sheet|excel/.test(t)) return "green";
      return "grey";
    },
  },
};
</script>

<style scoped>
.doc-row { border-top: 1px solid rgba(128, 128, 128, 0.25); padding: 10px 0; }
.doc-row:first-of-type { border-top: none; }
.doc-desc { white-space: pre-line; opacity: 0.85; }
</style>
